import { exportOrders, orderList } from 'api/order/order.js';
import BreadCrumb from 'components/common/BreadCrumb.js';
import { DataTable } from 'components/common/DataTable.js';
import ExportTable from 'components/common/ExportTable.js';
import Loader from 'components/common/Loader.js';
import NoDataFound from 'components/common/NoDataFound.js';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Container } from 'reactstrap';

export default function Order () {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({ total: 0, perPage: 10, currentPage: 1 });

  useEffect(() => {
    getOrderList({
      page: meta.currentPage,
      perPage: meta.perPage
    });
  }, [meta.currentPage, meta.perPage])

  async function getOrderList ({ page = 1, perPage = 10 }) {
    try{
      setLoading(true)
      const params = new URLSearchParams()

      params.append('page', page);
      params.append('perPage', perPage);
      const res = await orderList(`?${params.toString()}`)

      const resultData = res.data.map((item) => {
        return {
          id: item?.id,
          name: item?.name,
          order_date: item?.createdAt,
          cancelled_date: item?.cancelledAt,
          processed_date: item?.processedAt,
          closed_date: item?.closedAt,
          currency_code: item?.currencyCode,
          customer_email: item?.customer?.email,
          total_amount: item?.totalPriceSet?.shopMoney?.amount,
          item_amount: item?.subtotalPriceSet?.shopMoney?.amount,
          shipping_amount: item?.totalShippingPriceSet?.shopMoney?.amount,
          total_tax: item?.totalTaxSet?.shopMoney?.amount,
          items: item?.lineItems?.edges,
          fulfillment_status: item?.fulfillments[0]?.status,
          payment_status: item?.displayFinancialStatus
        }
      })
      setOrders(resultData)
      setMeta({
        total: res.meta.total,
        perPage: res.meta.perPage,
        currentPage: res.meta.currentPage
      });
    }catch(error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch order list');
    }finally{
      setLoading(false)
    }
  }

  const handlePageChange = (page) => {
    const updatedMeta = { ...meta, currentPage: page };
    setMeta(updatedMeta);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const updatedMeta = { ...meta, currentPage: page, perPage: newPerPage };
    setMeta(updatedMeta);
  };

  const handleExport = async (type) => {
    try {
      const response = await exportOrders(type);

      const contentType = response.headers['content-type'];

      const blob = new Blob([response.data], { type: contentType });

      const disposition = response.headers['content-disposition'];
      let filename = `orders.${type}`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File Downloaded Successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to export order list');
    }
  };

  const columns = [
    {
      name: 'Order',
      sortField: 'name',
      selector: row => row.name,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to={`/order/${row.id.split('/').pop()}`}>
            {row.name}
          </Link>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Items',
      sortField: 'items',
      selector: row => row.items,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.items.length}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Currency Code',
      selector: row => row.currency_code,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.currency_code}</span>
        </div>
      )
    },
    {
      name: 'Subtotal Amount',
      sortField: 'item_amount',
      selector: row => row.item_amount,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.item_amount}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Shipping Amount',
      sortField: 'shipping_amount',
      selector: row => row.shipping_amount,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.shipping_amount}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Tax Amount',
      sortField: 'total_tax',
      selector: row => row.total_tax,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.total_tax}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Total Amount',
      sortField: 'total_amount',
      selector: row => row.total_amount,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.total_amount}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Fulfillment Status',
      sortField: 'fulfillment_status',
      selector: row => row.fulfillment_status,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.fulfillment_status}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Payment Status',
      sortField: 'payment_status',
      selector: row => row.payment_status,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.payment_status}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Order Date',
      sortField: 'order_date',
      selector: row => row.order_date,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{dayjs(row.order_date).format('YYYY-MM-DD')}</span>
        </div>
      ),
      sortable: true
    },
    {
      name: 'Process Date',
      sortField: 'processed_date',
      selector: row => row.processed_date,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {row.processed_date ? <span>{dayjs(row.processed_date).format('YYYY-MM-DD')}</span> :
            <span>{'N/A'}</span>
          }
        </div>
      ),
      sortable: true
    },
    {
      name: 'Closed Date',
      sortField: 'closed_date',
      selector: row => row.closed_date,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {row.closed_date ? <span>{dayjs(row.closed_date).format('YYYY-MM-DD')}</span> :
            <span>{'N/A'}</span>
          }
        </div>
      ),
      sortable: true
    },
    {
      name: 'Cancelled Date',
      sortField: 'cancelled_date',
      selector: row => row.cancelled_date,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {row.cancelled_date ? <span>{dayjs(row.cancelled_date).format('YYYY-MM-DD')}</span> :
            <span>{'N/A'}</span>
          }
        </div>
      ),
      sortable: true
    },
    {
      name: 'Customer Email',
      sortField: 'customer_email',
      selector: row => row.customer_email,
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span>{row.customer_email}</span>
        </div>
      ),
      sortable: true
    }
  ];

  document.title = 'Order'

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center ongoingProjectsPg">
          <div className="mainPageHeader d-flex justify-content-between">
            <BreadCrumb title="Order List" />
          </div>
          <div className="d-flex justify-content-end ongoingSearchField">
            <div className="d-flex align-items-center me-3" style={{ gap: '10px' }}>
              <ExportTable handleExport={handleExport}/>
            </div>
          </div>
        </div>
        <div className="accordionTable my-4" style={{
          width: "100%",
          overflow: 'visible',
          position: 'relative',
          zIndex: 1
        }}>
          {
            orders.length > 0 && (
              <DataTable
                columns={columns}
                data={orders}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )
          }
          {orders.length === 0 && loading && <Loader/>}
          {orders.length === 0 && !loading && (
            <NoDataFound message="No orders available" />
          )}
        </div>
      </Container>
    </div>
  )
}
